import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TotemMark } from '@/shared/brand/TotemLogo';
import { Button } from '@/shared/ui/button';
import { useWorkstationStore } from '@/stores/workstationStore';
import { WorkstationKind } from '@totem/shared';

const PUBLIC_WORKSTATIONS_QUERY = gql`
  query PublicWorkstations {
    publicWorkstations {
      id
      branch_id
      kind
      display_name
    }
  }
`;

interface PublicWorkstation {
  id: string;
  branch_id: string;
  kind: keyof typeof GQL_WORKSTATION_KIND_TO_VALUE;
  display_name: string;
}

interface PublicWorkstationsResponse {
  publicWorkstations: PublicWorkstation[];
}

const GQL_WORKSTATION_KIND_TO_VALUE = {
  CASHIER: WorkstationKind.Cashier,
  CONSULTING_ROOM: WorkstationKind.ConsultingRoom,
  ADMIN: WorkstationKind.Admin,
  MIXED: WorkstationKind.Mixed,
} as const;

export function WorkstationSetupPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setWorkstation = useWorkstationStore((s) => s.setWorkstation);
  const { data, loading, error } = useQuery<PublicWorkstationsResponse>(PUBLIC_WORKSTATIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [selected_id, setSelectedId] = useState<string>('');

  const options: PublicWorkstation[] = data?.publicWorkstations ?? [];

  const onSubmitSetup = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const chosen = options.find((o) => o.id === selected_id);
    if (!chosen) return;
    const workstation_kind = GQL_WORKSTATION_KIND_TO_VALUE[chosen.kind];
    setWorkstation({
      workstation_id: chosen.id,
      workstation_kind,
      display_name: chosen.display_name,
    });
    navigate('/login');
  };

  return (
    <main className="bg-background flex h-full items-center justify-center px-4">
      <section className="w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-sm">
        <TotemMark className="mb-5 h-11 w-auto text-primary" title={t('common.app_name')} />
        <h1 className="font-display text-xl font-extrabold tracking-tight text-card-foreground">
          {t('workstation.setup_title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('workstation.setup_subtitle')}</p>

        <form className="mt-6 space-y-4" onSubmit={onSubmitSetup}>
          <label className="block text-sm">
            <span className="text-muted-foreground">{t('workstation.pick_label')}</span>
            <select
              value={selected_id}
              onChange={(e) => setSelectedId(e.target.value)}
              required
              disabled={loading || options.length === 0}
              className="border-input bg-background focus:ring-ring mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
            >
              <option value="" disabled>
                {loading ? t('common.loading') : t('workstation.pick_placeholder')}
              </option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.display_name} —{' '}
                  {t(`workstation.kind_${GQL_WORKSTATION_KIND_TO_VALUE[opt.kind]}`)}
                </option>
              ))}
            </select>
          </label>

          {error ? <p className="text-destructive text-sm">{t('workstation.load_error')}</p> : null}
          {!loading && !error && options.length === 0 ? (
            <p className="text-destructive text-sm">{t('workstation.none_available')}</p>
          ) : null}

          <Button type="submit" className="w-full" disabled={!selected_id}>
            {t('workstation.save_and_continue')}
          </Button>
        </form>
      </section>
    </main>
  );
}
